"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSession } from "next-auth/react";
import { BASE_PATH } from "@/constants";

export interface Profile {
  firstName: string | null;
  lastName: string | null;
  middleName?: string | null;
  driverLicense: string | null;
  phoneData?: PhoneData | null;
}

interface PhoneData {
  phoneNumber?: string;
  extension?: string;
  alternatePhoneNumber?: string;
  alternateExtension?: string;
}

interface ProfileContextValue {
  profile: Profile | null;
  loading: boolean;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(
  undefined,
);

function transformProfile(apiResponse: any): Profile {
  const details = apiResponse?.data?.details;
  const products = apiResponse?.data?.products;

  return {
    firstName: details?.firstName ?? null,
    lastName: details?.lastName ?? null,
    driverLicense: products?.driverLicense?.number ?? null,
    phoneData: details?.profilePhoneDetails,
  };
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProfile() {
      if (status !== "authenticated") {
        setProfile(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${BASE_PATH}/api/profile`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error("Failed to fetch profile");
        const apiData = await res.json();
        // console.log("profile API::", apiData);

        const flatProfile = transformProfile(apiData);
        // console.log("profile Flat::", flatProfile);

        setProfile(flatProfile);
      } catch (error) {
        console.error("Profile fetch error:", error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [status]);

  return (
    <ProfileContext.Provider value={{ profile, loading }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context)
    throw new Error("useProfile must be used within ProfileProvider");
  return context;
}
